import React, { useState, useEffect, useCallback } from "react";
import { useHistory } from "react-router-dom";
import { Col, Row, Statistic, Skeleton, Card, Table, DatePicker, message } from "antd";
import { getAnalytics } from "../../../services/applications.services";
import CardWizz from "../../../blocks/Card/Card";
import moment from "moment";
import { getItem } from "../../../helpers/localStorage";
import { USER } from "../../../constants/defaultKeys";
import "./Analytics.css";

const { RangePicker } = DatePicker;

const STATUS_LABELS = {
  Pending: "Pending",
  Submitted: "Submitted",
  InitialReview: "Initial Review",
  FinalReview: "Final Review",
  InformationRequired: "Information Required",
  Approved: "Approved",
  Rejected: "Rejected",
  Completed: "Completed",
  Recertify: "Recertify",
  Expired: "Expired",
};

function SimpleBarChart({ data, valueKey, labelKey, valueFormat }) {
  const max = Math.max(...data.map((d) => d[valueKey]), 1);
  return (
    <div className="analytics-bar-chart">
      {data.map((item, i) => (
        <div key={i} className="analytics-bar-chart__row">
          <span className="analytics-bar-chart__label">{item[labelKey]}</span>
          <div className="analytics-bar-chart__bar-wrap">
            <div
              className="analytics-bar-chart__bar"
              style={{ width: `${(item[valueKey] / max) * 100}%` }}
            />
          </div>
          <span className="analytics-bar-chart__value">
            {valueFormat ? valueFormat(item[valueKey]) : item[valueKey]}
          </span>
        </div>
      ))}
    </div>
  );
}

const Analytics = () => {
  const history = useHistory();
  const userDetails = JSON.parse(getItem(USER) || "{}");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState([
    moment().subtract(30, "days"),
    moment(),
  ]);

  useEffect(() => {
    if (!userDetails?.is_admin) {
      history.replace("/admin/dashboard");
      return;
    }
  }, [userDetails?.is_admin, history]);

  const fetchAnalytics = useCallback(() => {
    setLoading(true);
    const params = {
      start_date: dateRange[0].format("YYYY-MM-DD"),
      end_date: dateRange[1].format("YYYY-MM-DD"),
    };
    getAnalytics(params)
      .then((res) => {
        setData(res.data);
        setLoading(false);
      })
      .catch((err) => {
        message.error(err.response?.data?.message || "Failed to load analytics");
        setLoading(false);
      });
  }, [dateRange]);

  useEffect(() => {
    if (userDetails?.is_admin) fetchAnalytics();
  }, [fetchAnalytics, userDetails?.is_admin]);

  if (!userDetails?.is_admin) {
    return null;
  }

  if (loading && !data) {
    return (
      <div className="analytics-page">
        <Skeleton active paragraph={{ rows: 8 }} />
      </div>
    );
  }

  const summary = data?.summary || {};
  const pqqsByStatus = data?.pqqs_by_status || [];
  const revenueByCustomer = data?.revenue_by_customer || [];
  const pqqsOverTime = data?.pqqs_over_time || [];
  const revenueOverTime = data?.revenue_over_time || [];
  const period = data?.period || {};

  const pqqsByStatusColumns = [
    { title: "Status", dataIndex: "status", key: "status", render: (s) => STATUS_LABELS[s] || s },
    { title: "Count", dataIndex: "count", key: "count", width: 100 },
  ];

  const revenueByCustomerColumns = [
    { title: "Customer", dataIndex: "customer_name", key: "customer_name" },
    {
      title: "Revenue",
      dataIndex: "total",
      key: "total",
      width: 140,
      render: (v) => (v != null ? `$${Number(v).toLocaleString()}` : "—"),
    },
  ];

  return (
    <div className="analytics-page">
      <div className="analytics-page__header">
        <h1 className="analytics-page__title">Analytics</h1>
        <RangePicker
          value={dateRange}
          onChange={(dates) => dates && dates.length === 2 && setDateRange(dates)}
          allowClear={false}
        />
      </div>

      {/* Section 1: Summary cards */}
      <Card title="Summary" className="analytics-section">
        <Row gutter={[12, 12]}>
          <Col xs={24} sm={12} md={8} lg={8}>
            <CardWizz className="analytics-card">
              <Statistic
                title="Total revenue"
                value={summary.total_revenue}
                prefix="$"
                formatter={(v) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              />
            </CardWizz>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <CardWizz className="analytics-card">
              <Statistic title="Total vendors" value={summary.total_vendors} />
            </CardWizz>
          </Col>
          <Col xs={24} sm={12} md={8} lg={8}>
            <CardWizz className="analytics-card">
              <Statistic title="Total users" value={summary.total_users} />
            </CardWizz>
          </Col>
          <Col xs={24} sm={12} md={12} lg={8}>
            <CardWizz className="analytics-card">
              <Statistic
                title={`PQQs in period (${period.start_date} – ${period.end_date})`}
                value={summary.pqqs_last_30_days}
              />
            </CardWizz>
          </Col>
          <Col xs={24} sm={12} md={12} lg={8}>
            <CardWizz className="analytics-card">
              <Statistic
                title="Revenue in period"
                value={data?.revenue_in_period}
                prefix="$"
                formatter={(v) => Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              />
            </CardWizz>
          </Col>
        </Row>
      </Card>

      {/* Section 2: PQQs by status + approval rate */}
      <Card title="PQQs" className="analytics-section">
        {data?.approval_rate != null && (
          <p className="analytics-approval-rate">
            Approval rate in period: <strong>{data.approval_rate}%</strong>
          </p>
        )}
        <Table
          dataSource={pqqsByStatus}
          columns={pqqsByStatusColumns}
          rowKey="status"
          pagination={false}
          size="small"
        />
      </Card>

      {/* Section 3: Revenue by customer */}
      <Card title="Revenue by customer" className="analytics-section">
        {revenueByCustomer.length === 0 ? (
          <p className="analytics-empty">No revenue data by customer for this period.</p>
        ) : (
          <Table
            dataSource={revenueByCustomer}
            columns={revenueByCustomerColumns}
            rowKey="customer_name"
            pagination={false}
            size="small"
          />
        )}
      </Card>

      {/* Section 4: Trends */}
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={12}>
          <Card title="PQQs over time (by month)" className="analytics-section">
            {pqqsOverTime.length === 0 ? (
              <p className="analytics-empty">No PQQ data for this period.</p>
            ) : (
              <SimpleBarChart
                data={pqqsOverTime}
                labelKey="period"
                valueKey="count"
              />
            )}
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Revenue over time (by month)" className="analytics-section">
            {revenueOverTime.length === 0 ? (
              <p className="analytics-empty">No revenue data for this period.</p>
            ) : (
              <SimpleBarChart
                data={revenueOverTime}
                labelKey="period"
                valueKey="total"
                valueFormat={(v) => `$${Number(v).toLocaleString()}`}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Analytics;
